import React from 'react';
import RoadmapCoursePage from './RoadmapCoursePage';
import { ROADMAP_STUDENT_FAQS } from '../utils/roadmapStudentFaqs';

const JAVASCRIPT_ROADMAP_CONFIG = {
  path: '/roadmap-to-fullstack-javascript-course',
  seoTitle: 'Roadmap to Fullstack JavaScript Course | Algodoor',
  seoDescription: 'Visual roadmap for Fullstack JavaScript training with React, Node, projects, interview prep, and placement guidance.',
  badge: 'JavaScript Fullstack Roadmap',
  headingPrefix: 'Roadmap to Become a',
  headingHighlight: 'Fullstack JavaScript Developer',
  heroText: 'This is the exact learning path we follow in class. You start with frontend basics, move to backend APIs, build production-ready projects, then prepare for real interviews with guided practice.',
  roadmapNotice: 'This is one of our actively offered admission tracks and is strongly aligned with high-volume entry-level hiring needs.',
  enrollPath: '/courses',
  syllabusLink: '/syllabus/javascript-fullstack-roadmap.txt',
  prerequisites: [
    'Basic computer usage and confidence with typing/code editor',
    'School-level logic and English reading for documentation',
    'Willingness to practice at least 1-2 hours daily',
    'No prior framework knowledge required',
  ],
  quickStats: [
    { value: '12 Weeks', label: 'Structured Plan' },
    { value: '4 Projects', label: 'Portfolio Projects' },
    { value: '1:1', label: 'Mentor Guidance' },
  ],
  techStack: [
    {
      name: 'JavaScript',
      note: 'Core language + problem solving',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg',
    },
    {
      name: 'React + Next.js',
      note: 'Frontend and modern UI workflows',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg',
    },
    {
      name: 'Node.js + Express',
      note: 'Backend APIs and auth flows',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg',
    },
    {
      name: 'MongoDB',
      note: 'Data modeling and queries',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg',
    },
    {
      name: 'Firebase / Heroku',
      note: 'Deployment and hosting workflow',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg',
    },
  ],
  studentImages: [
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&auto=format&fit=crop&q=80',
  ],
  overviewCards: [
    {
      title: 'How Learning Happens',
      text: 'Every week is split into concept session, coding lab, and mentor review. You do not just watch tutorials; you build and present.',
    },
    {
      title: 'Syllabus Translation',
      text: 'HTML/CSS + JavaScript foundations, React ecosystem, Node APIs, database integration, deployment, and interview drills are covered in practical sequence.',
    },
    {
      title: 'Project Experience',
      text: 'You ship a task tracker, e-commerce module, admin dashboard, and one capstone fullstack app with authentication and role-based features.',
    },
    {
      title: 'Support Layer',
      text: 'Doubt sessions, code reviews, resume grooming, and mock interviews run parallel to technical modules from week 5 onward.',
    },
  ],
  roadmapSteps: [
    {
      title: 'Weeks 1-2: Web Basics + JavaScript Core',
      shortLabel: 'Basics',
      icon: '🟨',
      summary: 'Build the right fundamentals before touching frameworks.',
      bullets: [
        'Semantic HTML + responsive CSS layouts',
        'JavaScript logic, array methods, objects, async basics',
        'Mini tasks on DOM and API fetch',
      ],
    },
    {
      title: 'Weeks 3-5: React Frontend Track',
      shortLabel: 'Frontend',
      icon: '⚛️',
      summary: 'Create component-driven UIs and reusable state patterns.',
      bullets: [
        'Hooks, routing, state patterns, form handling',
        'Reusable UI components and dashboard screens',
        'Performance basics and clean folder structure',
      ],
    },
    {
      title: 'Weeks 6-8: Backend + Database',
      shortLabel: 'Backend',
      icon: '🟢',
      summary: 'Move into APIs, auth, and persistent data handling.',
      bullets: [
        'Express APIs, JWT auth, middleware and validation',
        'MongoDB schema design and CRUD operations',
        'Connect React frontend with backend services',
      ],
    },
    {
      title: 'Weeks 9-10: Capstone Build Sprint',
      shortLabel: 'Projects',
      icon: '🧩',
      summary: 'Deploy a production-style fullstack application.',
      bullets: [
        'Team-style project workflow with GitHub',
        'Deploy using Firebase Hosting or Heroku',
        'Testing, debugging and release checklist',
        'Project demo and mentor review panel',
      ],
      tooltip: 'You will deploy at least one app publicly and share a live link in your portfolio.',
    },
    {
      title: 'Weeks 11-12: Placement & Interview Prep',
      shortLabel: 'Interview',
      icon: '💼',
      summary: 'Turn project work into interview-ready confidence.',
      bullets: [
        'ATS-friendly resume and LinkedIn profile review',
        'JavaScript + React + API interview questions',
        'Mock interview feedback and improvement plan',
      ],
      tooltip: 'Focus on explaining project architecture in simple language during interviews.',
    },
  ],
  placementStats: [
    { label: 'Students with strong attendance + projects', value: 84 },
    { label: 'Interview shortlisting potential', value: 78 },
    { label: 'Entry-level hiring demand in JS stack', value: 82 },
  ],
  interviewFocus: [
    'JavaScript fundamentals: closures, promises, event loop',
    'React state patterns and component lifecycle',
    'REST APIs, authentication flow and middleware',
    'Git/GitHub workflow, pull requests, and team collaboration basics',
    'Deployment basics (Firebase/Heroku) and production troubleshooting',
    'DB schema decisions and real project trade-offs',
    'Debugging approach and communication clarity',
  ],
  jobRoles: [
    'Fullstack JavaScript Developer',
    'React Developer',
    'Node.js Developer',
    'MERN Stack Developer',
    'Frontend Engineer (Entry Level)',
  ],
  faqs: ROADMAP_STUDENT_FAQS,
};

const RoadmapFullstackJavaScriptPage = () => {
  return <RoadmapCoursePage config={JAVASCRIPT_ROADMAP_CONFIG} />;
};

export default RoadmapFullstackJavaScriptPage;
